// VelvetWarm bundle offers (blocks/velvet-offers.liquid): each item in the selected offer has its own
// Color and Size dropdowns and is added as its own variant. Item #1 drives the hidden main picker.
(() => {
  if (customElements.get('velvet-offers')) return;

  const SEPARATORS = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_with_space_separator: [2, ' ', ','],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
  };

  function cleanFormat(format) {
    const box = document.createElement('textarea');
    box.innerHTML = String(format || '');
    return box.value.replace(/<[^>]*>/g, '').trim();
  }

  function formatMoney(cents, format) {
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_match, key) => {
      const [decimals, thousands, decimal] = SEPARATORS[key] || SEPARATORS.amount;
      const [whole, fraction] = (Math.round(Number(cents) || 0) / 100).toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    });
  }

  function readJson(node, fallback) {
    try {
      return node ? JSON.parse(node.textContent || '') : fallback;
    } catch {
      return fallback;
    }
  }

  class VelvetOffers extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || document;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.moneyFormat = cleanFormat(readJson(this.querySelector('[data-money-format]'), ''));
      this.variants = readJson(this.querySelector('[data-pp-variants]'), []);
      this.notice = this.querySelector('[data-pp-notice]');
      this.currentVariant = this.variants.find((v) => String(v.id) === this.dataset.variantId) || this.variants[0] || null;
      this.mainState = null;
      this.busy = false;

      this.addEventListener('change', (event) => {
        const target = event.target;
        if (target instanceof HTMLInputElement && target.matches('.pp-offer__input')) {
          this.syncItems();
          this.apply();
        } else if (target instanceof HTMLSelectElement && target.matches('[data-pp-item-select]')) {
          this.onItemChange(target);
        }
      }, { signal });

      // The theme handles submit/click from a capture listener on document, so listen on
      // window (captured first) and stop the event before the theme adds the item as well.
      window.addEventListener('submit', this.onSubmit, { capture: true, signal });
      window.addEventListener('click', this.onBuyNowClick, { capture: true, signal });
      this.section.addEventListener('variant:update', this.onVariantUpdate, { signal });

      this.syncItems();
      this.apply();
      customElements.whenDefined('quantity-selector-component').then(() => this.apply());

      // The theme re-renders the buy buttons after a variant change and resets the quantity to 1.
      let queued = false;
      this.observer = new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          const input = this.productForm?.querySelector('input[name="quantity"]');
          if (input instanceof HTMLInputElement && input.value !== String(this.quantity)) this.apply();
        });
      });
      this.observer.observe(this.section, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
    }

    get quantity() {
      const checked = this.querySelector('.pp-offer__input:checked');
      return Math.max(1, parseInt(checked?.dataset.qty || '1', 10) || 1);
    }

    get productForm() {
      return this.section.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
    }

    get selectedCard() {
      return this.querySelector('.pp-offer__input:checked')?.closest('.pp-offer') || null;
    }

    // Push the offer quantity into the native form (the sticky bar reads it).
    apply() {
      const productForm = this.productForm;
      if (productForm) {
        const quantity = String(this.quantity);
        const selector = productForm.querySelector('quantity-selector-component');
        const input = productForm.querySelector('input[name="quantity"]');
        if (selector && typeof selector.setValue === 'function') selector.setValue(quantity);
        else if (input instanceof HTMLInputElement) input.value = quantity;
        productForm.dataset.quantityDefault = quantity;
      }
      this.updatePrices();
      this.updateImages();
      this.updateButtonLabel();
    }

    rowSelects(row) {
      return Array.from(row.querySelectorAll('[data-pp-item-select]'));
    }

    // Option values for an item row: its own selects, and the main selection for any other option.
    rowValues(row) {
      const values = [...(this.currentVariant?.options || [])];
      this.rowSelects(row).forEach((select) => {
        values[Number(select.dataset.option)] = select.value;
      });
      return values;
    }

    findVariant(values) {
      return this.variants.find((v) => v.options.every((opt, i) => opt === values[i])) || null;
    }

    // One variant (or null when the combination doesn't exist) per item of the card.
    cardVariants(card) {
      return Array.from(card.querySelectorAll('[data-pp-item]')).map((row) => this.findVariant(this.rowValues(row)));
    }

    // Item #1 and untouched items follow the main selection.
    syncItems() {
      const main = this.currentVariant?.options;
      this.querySelectorAll('[data-pp-item]').forEach((row) => {
        if (main && (row.getAttribute('data-index') === '0' || row.dataset.touched !== 'true')) {
          this.rowSelects(row).forEach((select) => {
            select.value = main[Number(select.dataset.option)];
          });
        }
        this.paintRow(row);
      });
      this.updateItemNotice();
    }

    // Disable values with no available variant for the row's other choices; show the color swatch.
    paintRow(row) {
      const values = this.rowValues(row);
      this.rowSelects(row).forEach((select) => {
        const index = Number(select.dataset.option);
        Array.from(select.options).forEach((option) => {
          const candidate = [...values];
          candidate[index] = option.value;
          const v = this.findVariant(candidate);
          option.disabled = !v || !v.available;
        });
        const swatch = select.parentElement?.querySelector('[data-pp-swatch]');
        const option = select.selectedOptions[0];
        if (swatch instanceof HTMLElement && option) {
          const image = option.dataset.swatchImage;
          swatch.style.setProperty('--pp-swatch', image ? `url("${image}")` : option.dataset.swatch || '#d1d5db');
        }
      });
      const v = this.findVariant(values);
      row.classList.toggle('pp-item--unavailable', !v || !v.available);
    }

    onItemChange(select) {
      const row = select.closest('[data-pp-item]');
      if (!row) return;
      row.dataset.touched = 'true';
      this.paintRow(row);
      if (row.getAttribute('data-index') === '0') this.selectMainOption(Number(select.dataset.option), select.value);
      this.updateItemNotice();
      this.updatePrices();
      this.updateImages();
    }

    // Select `value` for option `index` in the hidden main variant picker (gallery and price follow it).
    selectMainOption(index, value) {
      const picker = this.section.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
      const node = picker?.querySelector('.variant-picker__form')?.children[index];
      if (!node) return;
      const radio = Array.from(node.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);
      if (radio instanceof HTMLInputElement) {
        if (!radio.checked) radio.click();
        return;
      }
      const select = node.querySelector('select');
      if (select instanceof HTMLSelectElement && select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateItemNotice() {
      if (!this.notice) return;
      const card = this.selectedCard;
      const blocked = card ? this.cardVariants(card).some((v) => !v || !v.available) : false;
      let text = '';
      if (blocked) text = this.dataset.textItemUnavailable || this.dataset.textSoldout || '';
      else if (this.mainState === 'missing') text = this.dataset.textMissing || '';
      else if (this.mainState === 'soldout') text = this.dataset.textSoldout || '';
      this.notice.textContent = text;
      this.notice.hidden = !text;
    }

    updatePrices() {
      const showCompare = this.dataset.showCompare === 'true';
      this.querySelectorAll('.pp-offer__input').forEach((input) => {
        const card = input.closest('.pp-offer');
        if (!card) return;
        const qty = parseInt(input.dataset.qty || '1', 10) || 1;
        const discount = Math.min(90, Math.max(0, Number(input.dataset.discount) || 0));
        const items = this.cardVariants(card);
        let total;
        let compareTotal;
        if (items.length && items.every(Boolean)) {
          total = items.reduce((sum, v) => sum + v.price, 0);
          compareTotal = items.reduce((sum, v) => sum + Math.max(v.compare, v.price), 0);
        } else {
          const base = this.currentVariant;
          total = (base?.price || 0) * qty;
          compareTotal = Math.max(base?.compare || 0, base?.price || 0) * qty;
        }
        const final = discount > 0 ? Math.round((total * (100 - discount)) / 100) : total;
        const strike = discount > 0 ? total : compareTotal > total ? compareTotal : 0;
        const priceNode = card.querySelector('[data-pp-price]');
        const compareNode = card.querySelector('[data-pp-compare]');
        if (priceNode) priceNode.textContent = formatMoney(final, this.moneyFormat);
        if (compareNode) compareNode.textContent = showCompare && strike > final ? formatMoney(strike, this.moneyFormat) : '';
      });
    }

    // Card thumbnails show each item's chosen variant.
    updateImages() {
      this.querySelectorAll('.pp-offer').forEach((card) => {
        const items = this.cardVariants(card);
        card.querySelectorAll('[data-pp-img]').forEach((img, index) => {
          const src = items[index]?.image || this.currentVariant?.image;
          if (src && img instanceof HTMLImageElement && img.getAttribute('src') !== src) img.src = src;
        });
      });
    }

    updateButtonLabel() {
      const button = this.productForm?.querySelector('[ref="addToCartButton"]');
      if (!(button instanceof HTMLButtonElement) || button.disabled) return;
      const label = button.querySelector('.add-to-cart-text__content > span > span');
      if (!label) return;
      const quantity = this.quantity;
      const single = this.dataset.labelSingle || label.textContent || '';
      const multi = this.dataset.labelMulti || single;
      const text = (quantity > 1 && this.dataset.showQty === 'true' ? multi : single).replace(/\[qty\]/g, String(quantity));
      if (label.textContent !== text) label.textContent = text;
    }

    // Selected offer as cart lines, or null when an item can't be bought.
    cartLines() {
      const card = this.selectedCard;
      if (!card) return null;
      const items = this.cardVariants(card);
      if (!items.length || items.some((v) => !v || !v.available)) return null;
      const lines = new Map();
      items.forEach((v) => lines.set(v.id, (lines.get(v.id) || 0) + 1));
      return Array.from(lines, ([id, quantity]) => ({ id, quantity }));
    }

    onSubmit = (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !this.productForm?.contains(form)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const lines = this.cartLines();
      if (lines) this.addLines(lines, false);
      else this.updateItemNotice();
    };

    // Buy it now only knows one variant; add the whole offer and go to checkout instead.
    onBuyNowClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !this.productForm?.contains(target)) return;
      if (!target.closest('.shopify-payment-button, shopify-accelerated-checkout, shopify-buy-it-now-button')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const lines = this.cartLines();
      if (lines) this.addLines(lines, true);
      else this.updateItemNotice();
    };

    async addLines(lines, toCheckout) {
      if (this.busy) return;
      const productForm = this.productForm;
      const button = productForm?.querySelector('[ref="addToCartButton"]');
      this.busy = true;
      if (button instanceof HTMLButtonElement) button.setAttribute('aria-busy', 'true');
      // Ask for the cart sections so the cart drawer updates like it does for the native add.
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((node) => node.dataset.sectionId)
        .filter(Boolean);
      const root = window.Shopify?.routes?.root || '/';
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: lines,
            ...(sectionIds.length && !toCheckout ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {}),
          }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          if (this.notice) {
            this.notice.textContent = data.description || data.message || this.dataset.textItemUnavailable || '';
            this.notice.hidden = !this.notice.textContent;
          }
          return;
        }
        if (toCheckout) {
          window.location.href = `${root}checkout`;
          return;
        }
        let cart;
        try {
          cart = await (await fetch(`${root}cart.js`)).json();
        } catch {
          cart = undefined;
        }
        // Same event the native product form dispatches (header count, cart drawer).
        const cartEvent = new Event('cart:update', { bubbles: true });
        cartEvent.detail = {
          resource: cart,
          sourceId: productForm?.id || '',
          data: {
            source: 'velvet-offers',
            itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
            productId: this.dataset.productId,
            sections: data.sections,
          },
        };
        (productForm || this).dispatchEvent(cartEvent);
      } catch (error) {
        console.error(error);
      } finally {
        this.busy = false;
        if (button instanceof HTMLButtonElement) button.removeAttribute('aria-busy');
      }
    }

    onVariantUpdate = (event) => {
      const data = event.detail?.data;
      if (data?.productId && String(data.productId) !== String(this.dataset.productId)) return;
      const variant = event.detail?.resource || null;
      if (variant) this.currentVariant = this.variants.find((v) => v.id === variant.id) || this.currentVariant;
      this.mainState = !variant ? 'missing' : variant.available === false ? 'soldout' : null;
      this.syncItems();
      this.updatePrices();
      this.updateImages();
      // The product form morphs its button after this event; re-apply once it has.
      setTimeout(() => this.apply(), 0);
    };
  }

  customElements.define('velvet-offers', VelvetOffers);
})();